import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ScheduledTaskDetail from '../AI/components/ScheduledTaskDetail'
import TaskConfigPanel from '../AI/components/TaskConfigPanel'
import {
  fetchScheduledTasks,
  selectScheduledTasks,
  selectScheduledTasksStatus,
} from '../../features/openai/openaiSlice'
import { selectRootUser } from '../../rootSlice'
import { getSecretId } from '../Integrations/channels/generic/helpers'

/**
 * Detail view for a user's scheduled task inside the Task Library navigation.
 * Reuses the existing ScheduledTaskDetail (run history, pause/resume, run-now,
 * delete) and TaskConfigPanel (edit) so users keep the same controls without
 * being bounced out of /task-library to the AI Chat tab.
 */
export default function MyTaskDetail() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { taskId } = useParams<{ taskId: string }>()

  const tasks = useSelector(selectScheduledTasks) as any[]
  const status = useSelector(selectScheduledTasksStatus)
  const user = useSelector(selectRootUser) as any

  const agentKeyId = user?.id ? getSecretId(user.id, 'payhelm_ai_mcp') : ''

  const [editingTask, setEditingTask] = useState<any | null>(null)

  useEffect(() => {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      dispatch(fetchScheduledTasks() as any)
    }
  }, [dispatch, tasks])

  const handleBack = () => navigate('/task-library?tab=my')

  if (!taskId) {
    return (
      <div id="task-library-body" className="tw-min-h-screen tw-bg-white tw-py-8 tw-px-6">
        <p className="tw-text-sm tw-text-slate-600">No task selected.</p>
        <button
          onClick={handleBack}
          className="tw-mt-3 tw-text-sm tw-text-blue-600 hover:tw-underline"
        >
          Back to My Tasks
        </button>
      </div>
    )
  }

  const taskExists =
    Array.isArray(tasks) && tasks.some((t: any) => t.id === taskId)

  if (status === 'loading' && !taskExists) {
    return (
      <div id="task-library-body" className="tw-min-h-screen tw-bg-white tw-py-8 tw-px-6 tw-text-sm tw-text-slate-500">
        Loading task…
      </div>
    )
  }

  if (status !== 'loading' && !taskExists) {
    return (
      <div id="task-library-body" className="tw-min-h-screen tw-bg-white tw-py-8 tw-px-6">
        <p className="tw-text-sm tw-text-slate-600 tw-mb-3">
          This task isn't available.
        </p>
        <button
          onClick={handleBack}
          className="tw-text-sm tw-text-blue-600 hover:tw-underline"
        >
          Back to My Tasks
        </button>
      </div>
    )
  }

  return (
    <div id="task-library-body" className="tw-min-h-screen tw-bg-white">
      <ScheduledTaskDetail
        taskId={taskId}
        onBack={handleBack}
        onEditTask={(task) => setEditingTask(task)}
        compactActionBar={false}
      />
      {editingTask && (
        <>
          <div className="task-config-panel-open" aria-hidden="true" style={{ display: 'none' }} />
          <TaskConfigPanel
            isOpen={!!editingTask}
            onClose={() => setEditingTask(null)}
            editMode
            initialTask={editingTask}
            userEmail={user?.email || ''}
            keyId={agentKeyId}
          />
        </>
      )}
    </div>
  )
}
